import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Save, Search } from 'lucide-react';
import { Button } from '@/shared/ui/buttons/Button';
import { Input } from '@/shared/ui/inputs/Input';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { usePackagesListAll } from '@/entities/packages';
import { apiPartiesAddPackage } from '@/entities/parties';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { formatDate } from '@/shared/utils/date';
import { formatDecimalTrim } from '@/shared/utils/currency';
import type { ApiPackagesGetItem } from '@/shared/types/api/packages';

type Props = {
  partyId: string;
  /** Packages already attached to this party, so we hide them from the picker. */
  attachedPackages: ApiPackagesGetItem[];
};

const formatWeight = (row: ApiPackagesGetItem): string => {
  if (row.weight === null || row.weight === undefined || row.weight === '') return '—';
  const n = typeof row.weight === 'number' ? row.weight : Number(row.weight);
  if (!Number.isFinite(n)) return '—';
  const formatted = formatDecimalTrim(n);
  return `${formatted} ${row.weight_unit}`;
};

export const FeaturesPartiesAddPackageInline = ({ partyId, attachedPackages }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  // Local multi-select state: clicking "Qo'shish" on a row toggles it into the
  // staged set. Nothing hits the API until the user clicks the bottom "Saqlash"
  // — then we send one bulk call with every staged id.
  const [staged, setStaged] = useState<Set<string>>(new Set());

  const params = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
    }),
    [debouncedSearch],
  );

  // Fetch-all (every page): the endpoint has no server-side "unattached"
  // filter, so we filter `party_id === null` client-side below and must load
  // all pages or packages past the first page would be unreachable.
  const query = usePackagesListAll(params);

  const attachedIds = useMemo(
    () => new Set(attachedPackages.map((p) => p.id)),
    [attachedPackages],
  );

  // Eligible = unattached AND not already in this party. Once a row is in the
  // staged set we keep it visible (with the "selected" treatment) so the user
  // can untick it from the same list. Staged rows float to the top (in staged
  // order) so the selection mirrors the parcel picker's "selected first" layout.
  const availableRows = useMemo<ApiPackagesGetItem[]>(() => {
    const rows = query.data ?? [];
    const eligible = rows.filter((r) => r.party_id === null && !attachedIds.has(r.id));
    const stagedRows: ApiPackagesGetItem[] = [];
    const restRows: ApiPackagesGetItem[] = [];
    eligible.forEach((r) => {
      if (staged.has(r.id)) stagedRows.push(r);
      else restRows.push(r);
    });
    return [...stagedRows, ...restRows];
  }, [query.data, attachedIds, staged]);

  const toggleStaged = (id: string): void =>
    setStaged((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const saveMutation = useMutate({
    api: (ids: string[]) =>
      apiPartiesAddPackage(partyId, { package_ids: ids }),
  });

  const handleSave = async (): Promise<void> => {
    const ids = Array.from(staged);
    if (ids.length === 0) return;
    try {
      const res = await saveMutation.mutateAsync(ids);
      if (res.added > 0) {
        toast.success(
          t('parties.addPackageInline.toast.bulkAdded', { count: res.added }),
        );
      }
      if (res.skipped.length > 0) {
        const reason = res.skipped[0]?.reason;
        const reasonLabel = reason
          ? t(`parties.addPackage.skipReason.${reason}`)
          : t('parties.addPackage.skipReason.not_found');
        toast.info(
          t('parties.addPackageInline.toast.bulkSkipped', {
            count: res.skipped.length,
            reason: reasonLabel,
          }),
        );
      }
      setStaged(new Set());
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      void qc.invalidateQueries({ queryKey: queryKeys.parties.all });
      void qc.invalidateQueries({ queryKey: queryKeys.packages.all });
      void qc.invalidateQueries({ queryKey: queryKeys.parcels.all });
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold text-ink">
          {t('parties.addPackageInline.title')}
        </h3>
        <p className="text-xs text-ink-muted">
          {t('parties.addPackageInline.subtitle')}
        </p>
      </div>

      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('parties.addPackage.searchPlaceholder')}
        prefix={<Search className="h-4 w-4" />}
      />

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {query.isLoading ? (
          <div className="flex min-h-[160px] items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : query.error ? (
          <div className="p-4">
            <ErrorState
              message={extractErrorMessage(query.error, t('packages.list.loadError'))}
              onRetry={() => void query.refetch()}
            />
          </div>
        ) : availableRows.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title={t('parties.addPackageInline.emptyTitle')}
              description={t('parties.addPackageInline.emptyDescription')}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('parties.addPackageInline.addRow')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('parties.detail.packagesColumns.name')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('parties.detail.packagesColumns.parcelsCount')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('parties.detail.packagesColumns.weight')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('parties.detail.packagesColumns.createdAt')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {availableRows.map((row) => {
                  const isStaged = staged.has(row.id);
                  return (
                    <tr
                      key={row.id}
                      className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50"
                    >
                      <td className="px-3 py-2.5">
                        <Button
                          type="button"
                          size="sm"
                          variant={isStaged ? 'outline' : 'primary'}
                          leftIcon={
                            isStaged ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Plus className="h-4 w-4" />
                            )
                          }
                          onClick={() => toggleStaged(row.id)}
                        >
                          {isStaged
                            ? t('parties.addPackageInline.selected')
                            : t('parties.addPackageInline.addRow')}
                        </Button>
                      </td>
                      <td className="px-3 py-2.5 font-medium text-ink">
                        <span className="block max-w-[260px] truncate">{row.name}</span>
                      </td>
                      <td className="px-3 py-2.5 text-ink-soft">{row.parcels_count}</td>
                      <td className="px-3 py-2.5 text-ink-soft">{formatWeight(row)}</td>
                      <td className="px-3 py-2.5 text-ink-soft">
                        {formatDate(row.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-ink-muted">
          {t('parties.addPackageInline.selectedCount', { count: staged.size })}
        </span>
        <Button
          type="button"
          variant="primary"
          leftIcon={<Save className="h-4 w-4" />}
          loading={saveMutation.isPending}
          disabled={staged.size === 0 || saveMutation.isPending}
          onClick={() => void handleSave()}
        >
          {t('parties.addPackageInline.save')}
        </Button>
      </div>
    </div>
  );
};
